import { useAuth0 } from '@auth0/auth0-react';
import { useQuery } from '@tanstack/react-query';

import { API_URL, apiFetch } from '@/config/api';
import type { Feedback } from '@/types/feedback';

interface FeedbackResponse {
  feedbacks: Feedback[];
  total: number;
}

export function useFeedback(page: number = 1, limit: number = 50) {
  const { getAccessTokenSilently } = useAuth0();

  return useQuery({
    queryKey: ['feedback', page, limit],
    queryFn: async (): Promise<FeedbackResponse> => {
      const token = await getAccessTokenSilently();

      const response = await apiFetch(
        `${API_URL}/admin/feedback?page=${page}&limit=${limit}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.detail?.user_error_message || 'Failed to fetch feedback'
        );
      }

      const feedbacks = await response.json();

      // Also fetch total count
      const countResponse = await apiFetch(`${API_URL}/admin/feedback/count`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const countData = await countResponse.json();

      return {
        feedbacks,
        total: countData.total_count || 0,
      };
    },
    enabled: true,
  });
}
